'use client'

import { useEffect, useState, useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { ShimmerButton } from './ui/shimmer-button';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';

interface CTAButtonProps {
  location: string;
  path?: string;
  children: React.ReactNode;
  variant?: 'shimmer' | 'button';
  className?: string;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  showArrow?: boolean;
  ariaLabel?: string;
  openInNewTab?: boolean;
}

export function CTAButton({ 
  children, 
  location, 
  path = '', 
  variant = 'shimmer',
  className = '',
  size = 'default',
  buttonVariant = 'default',
  showArrow = true,
  ariaLabel,
  openInNewTab = true
}: CTAButtonProps) {
  const posthog = usePostHog();
  const [distinctId, setDistinctId] = useState<string | null>(null);

  useEffect(() => {
    if (!posthog) return;
    
    // Try to get ID immediately
    const id = posthog.get_distinct_id();
    
    if (id) {
      setDistinctId(id);
      return;
    }
    
    // If not ready yet, subscribe and try again once session initializes
    const off = posthog.onSessionId(() => {
      const sessionId = posthog.get_distinct_id();
      setDistinctId(sessionId || null);
    });
    
    return off;
  }, [posthog]);
  const handleCTAClick = useCallback(() => {
    // Get the current distinct ID (from state or direct call)
    const currentId = posthog?.get_distinct_id() || distinctId;
    
    // Capture the event
    if (posthog) {
      posthog.capture('marketing:enter_app_clicked', {
        button_location: location,
        distinct_id_passed: currentId || 'not_available'
      });
    }

    // Build target URL - only add ph_distinct_id if we have a valid ID
    const baseUrl = `https://app.fashn.ai${path}`;
    const targetUrl = currentId ? `${baseUrl}?ph_distinct_id=${encodeURIComponent(currentId)}` : baseUrl;
    
    // Navigate to target URL
    if (openInNewTab) {
      window.open(targetUrl, '_blank');
    } else {
      window.location.href = targetUrl;
    }
  }, [posthog, distinctId, location, path, openInNewTab]);

  if (variant === 'shimmer') {
    return (
      <ShimmerButton 
        onClick={handleCTAClick} 
        className={className}
        aria-label={ariaLabel}
      >
        {children}
        {showArrow && (
          <ArrowRight
            size={18}
            className="group-hover:translate-x-1 transition-all ml-2"
          />
        )}
      </ShimmerButton>
    );
  }

  return (
    <Button 
      onClick={handleCTAClick} 
      variant={buttonVariant} 
      size={size} 
      className={showArrow ? `group flex gap-2 items-center ${className}` : className}
      aria-label={ariaLabel}
    >
      {children}
      {showArrow && (
        <ArrowRight
          size={18}
          className="group-hover:translate-x-1 transition-all"
        />
      )}
    </Button>
  );
}